package com.nonopn.skydome.common.app.home

import androidx.compose.material.ScaffoldState
import com.dokar.sonner.TextToastAction
import com.dokar.sonner.ToastType
import com.dokar.sonner.ToasterState
import com.nonopn.skydome.common.app.api.Mode
import com.nonopn.skydome.common.app.api.impl.ApiAdmin
import com.nonopn.skydome.common.app.api.impl.ApiInterface
import com.nonopn.skydome.common.app.api.impl.ApiNormal
import com.nonopn.skydome.common.app.api.impl.ThunderDetectionEstimate
import com.nonopn.skydome.common.app.api.model.DashboardImpactReport
import com.nonopn.skydome.common.app.api.model.DashboardImpactReportV2
import com.nonopn.skydome.common.app.checkout.CheckoutActivationPolling
import com.nonopn.skydome.common.app.checkout.CheckoutLoginGuard
import com.nonopn.skydome.common.app.checkout.CheckoutReturn
import com.nonopn.skydome.common.app.checkout.CheckoutReturnPhase
import com.nonopn.skydome.common.app.checkout.checkoutAttemptStore
import com.nonopn.skydome.common.app.checkout.checkoutCurrentQuery
import com.nonopn.skydome.common.app.checkout.clearCheckoutParams
import com.nonopn.skydome.common.app.checkout.isCheckoutActivated
import com.nonopn.skydome.common.app.checkout.loginRequest
import com.nonopn.skydome.common.app.checkout.parseCheckoutReturn
import com.nonopn.skydome.common.app.detection.ThunderDetection
import com.nonopn.skydome.common.app.navigation.PossibleRoutes
import com.nonopn.skydome.common.app.navigation.RouteDashboard
import com.nonopn.skydome.common.app.navigation.RouterDashboard
import com.nonopn.skydome.common.app.navigation.RouterEstimatesAdmin
import com.nonopn.skydome.common.app.navigation.RouterSettings
import com.nonopn.skydome.common.app.pages.documents.DocumentsPerRequest
import com.nonopn.skydome.common.app.pages.documents.mergeDocumentHistories
import com.nonopn.skydome.common.app.pages.documents.missingOnServer
import com.nonopn.skydome.common.app.pages.documents.toApi
import com.nonopn.skydome.common.app.pages.documents.toModel
import com.nonopn.skydome.common.app.pages.main.model.DocumentHistoryItem
import com.nonopn.skydome.common.app.pages.main.model.SpecificData
import com.nonopn.skydome.common.app.pages.main.model.api.ApiAnswerProjectWithThunderActivities
import com.nonopn.skydome.common.app.pages.probes.loadAccountStatus
import com.nonopn.skywatcher.app.utils.AppBackPressProvider
import com.nonopn.skywatcher.app.utils.AuthentInit
import com.nonopn.skywatcher.app.utils.safeLaunch
import com.nonopn.skywatcher.common.ILoginCaller
import com.nonopn.skywatcher.common.SessionManager
import com.nonopn.skywatcher.common.api.ApiProvider
import com.nonopn.skywatcher.common.api.ApiSession
import com.nonopn.skywatcher.common.api.AuthError
import com.nonopn.skywatcher.common.api.ChangePasswordRequest
import com.nonopn.skywatcher.common.api.CheckoutLoginRequest
import com.nonopn.skywatcher.common.api.DocumentsHistoryRequest
import com.nonopn.skywatcher.common.api.MinPasswordLength
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.app_title
import com.nonopn.skywatcher.common.app.res.warn_customer_text_client
import com.nonopn.skywatcher.common.app.res.warn_customer_text_manager
import com.nonopn.skywatcher.config.SharedConfig
import com.nonopn.skywatcher.design.appbar.AppBarState
import com.nonopn.skywatcher.design.appbar.AppBarStateProvider
import com.nonopn.skywatcher.design.appbar.FloatingActionButtonState
import com.nonopn.skywatcher.design.init.InitStateProvider
import eu.codlab.navigation.NavigateTo
import eu.codlab.navigation.Navigation
import eu.codlab.navigation.NavigationListener
import eu.codlab.navigation.NavigatorModel
import eu.codlab.navigation.RouteParameterTo
import eu.codlab.sentry.wrapper.Sentry
import com.nonopn.skydome.common.app.utils.setSystemLocale
import eu.codlab.viewmodel.StateViewModel
import eu.codlab.viewmodel.launch
import korlibs.time.DateTime
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import org.jetbrains.compose.resources.StringResource
import kotlin.time.Duration
import kotlin.time.Duration.Companion.seconds

// Local cache of the documents history (the full history lives on the server)
private const val MaxDocumentHistorySize = 200

@Suppress("TooManyFunctions")
abstract class AppModel(
    val isSystemDarkMode: Boolean
) : StateViewModel<AppModelState>(
    AppModelState(
        defaultRoute = RouteDashboard,
        currentRoute = RouteDashboard,
        isSystemDarkMode = isSystemDarkMode
    )
),
    AppBarStateProvider,
    InitStateProvider,
    ILoginCaller,
    NavigationListener {
    val appTitle = Res.string.app_title
    var onBackPressed: AppBackPressProvider = AppBackPressProvider()

    var toaster: ToasterState? = null

    private val sessionManager = SessionManager(
        folder = "StrikeRadar",
        SpecificData.serializer(),
        admin = { ApiAdmin(it) },
        normal = { ApiNormal(it) },
    )

    // ── Return from the Stripe Checkout ───────────────────────────────────────
    // The one time code of the success URL is sent once: the guard keeps the result of the first call
    // (null = no session) for any later attempt, and a code already sent from this tab is never sent again.
    private val checkoutLoginGuard = CheckoutLoginGuard<ApiProvider<ApiInterface>?>(
        store = checkoutAttemptStore(),
        alreadyAttempted = null
    )
    private val checkoutScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var checkoutActivationJob: Job? = null

    var navigator: NavigatorModel? = null
    var scaffoldState: ScaffoldState? = null

    // Project UUID to highlight when navigating to ProjectsScreen from ProbesScreen
    var pendingHighlightProjectUuid: String? = null

    // Probe to select on ProbesScreen when navigating from the dashboard map pop-up
    var pendingProbeUuid: String? = null

    // Opens the probe creation screen in trial mode (one single probe) when true
    var pendingProbeTrial: Boolean = false

    // Search to prefill on the documents screen when navigating from the dashboard map pop-up
    var pendingDocumentsSearch: String? = null

    abstract val warnCustomerText: StringResource

    companion object {
        fun fake() = object : AppModel(false) {
            override val warnCustomerText: StringResource
                get() = Res.string.warn_customer_text_client

            override fun warnCustomerAction() {
                // nothing
            }
        }
    }

    fun showToast(
        title: String,
        action: String,
        type: ToastType = ToastType.Normal,
        duration: Duration = 20.seconds,
        onClick: () -> Unit = { /* nothing */ }
    ) {
        safeLaunch {
            toaster?.show(
                message = title,
                type = type,
                action = TextToastAction(
                    text = action,
                    onClick = { toast ->
                        onClick()

                        toaster?.dismiss(toast)
                    },
                ),
                duration = duration
            )
        }
    }

    override fun isInitialized() = states.value.initialized

    abstract fun warnCustomerAction()

    @Suppress("TooGenericExceptionCaught", "SwallowedException", "LongMethod", "CyclomaticComplexMethod")
    override fun initialize() {
        safeLaunch(
            onError = {
                it.printStackTrace()
            }
        ) {
            // Shown at once from the URL alone, before any session is opened
            val checkoutReturn = parseCheckoutReturn(checkoutCurrentQuery())
            when (checkoutReturn) {
                is CheckoutReturn.Success -> setCheckoutPhase(CheckoutReturnPhase.SigningIn)
                is CheckoutReturn.Cancel -> {
                    // No code on a cancel: no login attempt
                    clearCheckoutParams()
                    setCheckoutPhase(CheckoutReturnPhase.Cancelled)
                }

                null -> Unit
            }

            var apiProvider = sessionManager.initialize()

            if (checkoutReturn is CheckoutReturn.Success) {
                // A session already open needs no code: the code is only used to open one
                if (null == apiProvider) {
                    apiProvider = checkoutReturn.loginRequest()?.let { request ->
                        checkoutLoginGuard.runOnce(request.code) { checkoutLogin(request) }
                    }
                }
                clearCheckoutParams()
                setCheckoutPhase(
                    if (null == apiProvider) CheckoutReturnPhase.LoginNeeded else CheckoutReturnPhase.Activating
                )
            }

            Sentry.init {
                it.dsn = SharedConfig.sentrySecondary
            }

            /*GoogleAuthProvider.create(
                credentials = GoogleAuthCredentials(
                    serverId = SharedConfig.googleAuthServerId
                )
            )*/

            AuthentInit.initialize()

            val currentRoute = PossibleRoutes.defaultFromUrlLaunched(Navigation.originalPath())
            val adminModePreferred = sessionManager.getSpecificData()?.adminModePreferred == true

            updateState {
                copy(
                    initialized = true,
                    apiProvider = apiProvider,
                    defaultRoute = currentRoute,
                    currentRoute = currentRoute,
                    currentApi = apiProvider?.apiNormal,
                    thunderDetections = thunderDetections + ThunderDetection.fakes
                )
            }

            // The preference is only applied when the server gave an admin API to this session
            if (adminModePreferred && apiProvider?.apiAdmin != null) {
                applyAdminMode(true)
            }

            if (states.value.checkoutReturn == CheckoutReturnPhase.Activating) {
                startCheckoutActivationPolling()
            }

            onReceiveMessage()
        }
    }

    /** POST /api/checkout/login then session from the token; null on any error (the user logs in as usual). */
    @Suppress("TooGenericExceptionCaught")
    private suspend fun checkoutLogin(request: CheckoutLoginRequest): ApiProvider<ApiInterface>? = try {
        sessionManager.loginWithCheckoutCode(request)
    } catch (e: CancellationException) {
        throw e
    } catch (_: Throwable) {
        // InvalidLoginCode (expired, already used, lost by a server restart) or any other error.
        // Never logged: neither the code nor the answer.
        null
    }

    private fun setCheckoutPhase(phase: CheckoutReturnPhase?) {
        updateState { copy(checkoutReturn = phase) }
    }

    /** Hides the checkout banner and stops the activation polling. */
    fun dismissCheckoutReturn() {
        checkoutActivationJob?.cancel()
        checkoutActivationJob = null
        setCheckoutPhase(null)
    }

    /**
     * Polls GET /api/account/status (every 3 s, 2 min at most) until the purchase appears: the probes, the
     * subscription or the credits are activated by the Stripe webhook, a few seconds after the payment.
     */
    private fun startCheckoutActivationPolling() {
        checkoutActivationJob?.cancel()
        setCheckoutPhase(CheckoutReturnPhase.Activating)
        checkoutActivationJob = checkoutScope.launch {
            var baselineCredits: Int? = null
            repeat(CheckoutActivationPolling.MaxAttempts) {
                val status = loadAccountStatus()
                if (status != null) {
                    if (isCheckoutActivated(status, baselineCredits)) {
                        invalidateDashboardCache()
                        setCheckoutPhase(CheckoutReturnPhase.Activated)
                        return@launch
                    }
                    if (null == baselineCredits) baselineCredits = status.strikeRadar?.nsgCredits
                }
                delay(CheckoutActivationPolling.IntervalMillis)
            }
            setCheckoutPhase(CheckoutReturnPhase.ActivationDelayed)
        }
    }

    @Suppress("SwallowedException", "TooGenericExceptionCaught")
    private fun onReceiveMessage() = safeLaunch {
        // nothing
    }

    override fun setAppBarState(appBarState: AppBarState) {
        safeLaunch {
            updateState {
                println("setAppBarState")
                copy(appBarState = appBarState)
            }
        }
    }

    fun setFloatingActionButton(floatingActionButtonState: FloatingActionButtonState) = safeLaunch {
        updateState {
            copy(floatingActionButtonState = floatingActionButtonState)
        }
    }

    fun setLanguage(code: String) {
        setSystemLocale(code)
        updateState { copy(appLanguage = code) }
    }

    fun popBackStack() {
        navigator?.navigateUp()
    }

    fun floatingActionButton(): FloatingActionButtonState? = states.value.floatingActionButtonState

    var mainCompositionScope: CoroutineScope? = null

    fun show(navigateTo: NavigateTo) {
        // Note: the navigator's own navigateTo() already handles popBackStack
        // via the navigateTo.stack.popBackStack flag — no manual pop needed here.
        navigator?.navigateTo(navigateTo)

        mainCompositionScope?.launch {
            scaffoldState?.drawerState?.close()
        }

        updateState {
            copy(
                currentRoute = navigateTo.route,
                appBarState = AppBarState.Regular(),
                floatingActionButtonState = null
            )
        }
    }

    /**
     * Records the route really displayed (after a back navigation too) so that the active entry of the menu
     * follows it. Unlike [show], this neither navigates nor resets the app bar.
     */
    fun markCurrentRoute(route: RouteParameterTo) {
        if (states.value.currentRoute != route) {
            updateState { copy(currentRoute = route) }
        }
    }

    /**
     * Called by an administrator screen reached directly (link, reload): non admin accounts go back to the
     * dashboard, admin accounts get the administrator mode and its estimates list.
     */
    fun leaveAdminRoute() {
        if (states.value.apiProvider?.apiAdmin != null) {
            enterAdminMode()
        } else {
            show(RouterDashboard.navigateTo())
        }
    }

    /** Switches to the administrator mode (menu and API) when the session is admin, without navigating. */
    fun ensureAdminMode() {
        val state = states.value
        if (state.apiProvider?.apiAdmin != null && !state.isAdminMode) {
            applyAdminMode(true)
        }
    }

    override fun shown(navigateTo: NavigateTo) {
        // nothing yet, but using above ?
    }

    private val specificDataMutex = Mutex()

    fun writeSpecificData(specificData: SpecificData) = launch {
        specificDataMutex.withLock {
            val stored = sessionManager.getSpecificData()
            val currentHistory = stored?.documentHistory.orEmpty()
            sessionManager.saveSpecificData(
                specificData.copy(
                    documentHistory = specificData.documentHistory.ifEmpty { currentHistory },
                    adminModePreferred = stored?.adminModePreferred ?: false
                )
            )
        }
    }

    fun recordDocumentHistory(item: DocumentHistoryItem) = launch {
        specificDataMutex.withLock {
            val current = sessionManager.getSpecificData() ?: SpecificData()
            val updatedHistory = (listOf(item) + current.documentHistory.filterNot { it.id == item.id })
                .take(MaxDocumentHistorySize)
            sessionManager.saveSpecificData(current.copy(documentHistory = updatedHistory))
        }

        // Also keep it on the server (per account). A failure is silent: the item stays in the local
        // cache and is sent again by the next syncDocumentHistory.
        attemptServerCall { session, token ->
            session.documentsHistory(token, DocumentsHistoryRequest(listOf(item.toApi())))
        }
    }

    /**
     * Synchronizes the documents history with the server: sends the [local] items the server does not know
     * (history recorded before the server storage existed, or failed uploads), then refreshes the local cache.
     * Returns the full merged history, or null when the server cannot be reached (the local cache stays valid).
     */
    suspend fun syncDocumentHistory(local: List<DocumentHistoryItem>): List<DocumentHistoryItem>? {
        var server = attemptServerCall { session, token ->
            session.documentsHistory(token).items.mapNotNull { it.toModel() }
        } ?: return null

        missingOnServer(local, server).chunked(DocumentsPerRequest).forEach { chunk ->
            attemptServerCall { session, token ->
                session.documentsHistory(token, DocumentsHistoryRequest(chunk.map { it.toApi() }))
                    .items.mapNotNull { it.toModel() }
            }?.let { server = it }
        }

        val merged = mergeDocumentHistories(local, server)

        specificDataMutex.withLock {
            val current = sessionManager.getSpecificData() ?: SpecificData()
            val cache = mergeDocumentHistories(current.documentHistory, merged).take(MaxDocumentHistorySize)
            sessionManager.saveSpecificData(current.copy(documentHistory = cache))
        }

        return merged
    }

    @Suppress("TooGenericExceptionCaught", "SwallowedException")
    private suspend fun <T> attemptServerCall(block: suspend (ApiSession, String) -> T): T? {
        val session = states.value.apiProvider?.apiSession ?: return null

        return try {
            block(session, session.profile().persistenceToken)
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (_: Throwable) {
            null
        }
    }

    @Suppress("TooGenericExceptionCaught")
    override fun register(email: String, password: String, callback: (err: Throwable?) -> Unit) {
        launch(
            onError = {
                callback(it)
            }
        ) {
            try {
                // Same rule as the backend (PasswordTooShort): no request for a password it would refuse
                if (password.length < MinPasswordLength) {
                    callback(authErrorException(AuthError.PasswordTooShort))
                    return@launch
                }

                val apiProvider = sessionManager.register(email, password)

                updateState {
                    copy(
                        apiProvider = apiProvider,
                        currentApi = apiProvider.apiNormal
                    )
                }

                callback(null)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (err: Throwable) {
                // Translated message from the backend `code` (never the raw English message, never credentials)
                callback(err.toTranslatedAuthError())
            }
        }
    }

    @Suppress("TooGenericExceptionCaught")
    override fun login(email: String, password: String, callback: (err: Throwable?) -> Unit) {
        launch(
            onError = {
                callback(it)
            }
        ) {
            try {
                val apiProvider = sessionManager.login(email, password)

                updateState {
                    copy(
                        apiProvider = apiProvider,
                        currentApi = apiProvider.apiNormal
                    )
                }

                // Paid but the code could not open the session: the activation is followed once logged in
                if (states.value.checkoutReturn == CheckoutReturnPhase.LoginNeeded) {
                    startCheckoutActivationPolling()
                }

                callback(null)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (err: Throwable) {
                // Translated message from the backend `code` (never the raw English message, never credentials)
                callback(err.toTranslatedAuthError())
            }
        }
    }

    @Suppress("TooGenericExceptionCaught")
    fun changePassword(
        oldPassword: String,
        newPassword: String,
        confirmPassword: String,
        callback: (err: Throwable?) -> Unit
    ) {
        launch(
            onError = {
                callback(it)
            }
        ) {
            try {
                val current = states.value.apiProvider
                    ?: throw IllegalStateException("No active session")
                val isAdminMode = states.value.currentApi?.mode == Mode.Admin

                val apiProvider = sessionManager.changePassword(
                    apiSession = current.apiSession,
                    request = ChangePasswordRequest(
                        password = newPassword,
                        passwordConfirm = confirmPassword,
                        passwordOld = oldPassword
                    ),
                    generateWithAdmin = current.apiAdmin != null
                )

                updateState {
                    copy(
                        apiProvider = apiProvider,
                        currentApi = if (isAdminMode) apiProvider.apiAdmin else apiProvider.apiNormal
                    )
                }

                callback(null)
            } catch (err: Throwable) {
                callback(err)
            }
        }
    }

    suspend fun getSpecificData() = sessionManager.getSpecificData()

    fun switchToMode(mode: Mode) {
        updateState {
            copy(
                currentApi = when (mode) {
                    Mode.Normal -> this.apiProvider?.apiNormal
                    Mode.Admin -> this.apiProvider?.apiAdmin
                }
            )
        }
    }

    /**
     * Enters the administrator mode. Only accounts that own an admin API can do it: for any other
     * account this does nothing.
     */
    fun enterAdminMode() {
        if (states.value.apiProvider?.apiAdmin == null) return

        applyAdminMode(true)
        launch { rememberAdminModePreference(true) }
        // The estimates list is only reachable from the admin menu, so it is always created fresh
        show(RouterEstimatesAdmin.navigateTo())
    }

    /** Goes back to the regular user mode (account page, which is not part of the admin menu). */
    fun exitAdminMode() {
        applyAdminMode(false)
        launch { rememberAdminModePreference(false) }
        show(RouterSettings.navigateTo())
    }

    private suspend fun rememberAdminModePreference(preferred: Boolean) {
        specificDataMutex.withLock {
            val current = sessionManager.getSpecificData() ?: SpecificData()
            sessionManager.saveSpecificData(current.copy(adminModePreferred = preferred))
        }
    }

    private fun applyAdminMode(admin: Boolean) {
        updateState {
            copy(
                isAdminMode = admin,
                currentApi = if (admin) {
                    this.apiProvider?.apiAdmin ?: this.apiProvider?.apiNormal
                } else {
                    this.apiProvider?.apiNormal
                }
            )
        }
        // Reports differ between the user and the admin API
        invalidateDashboardCache()
    }

    fun logout() = launch {
        dismissCheckoutReturn()
        rememberAdminModePreference(false)
        sessionManager.logout()
        updateState {
            copy(apiProvider = null, currentApi = null, isAdminMode = false)
        }
    }

    // ── Dashboard reports cache ───────────────────────────────────────────────
    // Fetched once per session; shared by DashboardModel, DocumentsModel, AccountModel.
    // Call invalidateDashboardCache() to force a refresh (e.g. pull-to-refresh).

    private val dashboardCacheMutex = Mutex()
    private var dashboardReportsCache: List<DashboardImpactReport>? = null

    suspend fun cachedDashboardReports(): List<DashboardImpactReport> {
        val api = states.value.currentApi ?: return emptyList()
        dashboardReportsCache?.let { return it }
        if (!dashboardCacheMutex.tryLock()) return dashboardReportsCache ?: emptyList()
        return try {
            dashboardReportsCache
                ?: api.dashboardProbeReport().also { dashboardReportsCache = it }
        } finally {
            dashboardCacheMutex.unlock()
        }
    }

    private var dashboardReportsV2Cache: List<DashboardImpactReportV2>? = null

    suspend fun cachedDashboardReportsV2(): List<DashboardImpactReportV2> {
        val api = states.value.currentApi ?: return emptyList()
        dashboardReportsV2Cache?.let { return it }
        if (!dashboardCacheMutex.tryLock()) return dashboardReportsV2Cache ?: emptyList()
        return try {
            dashboardReportsV2Cache
                ?: api.dashboardProbeReportV2().also { dashboardReportsV2Cache = it }
        } finally {
            dashboardCacheMutex.unlock()
        }
    }

    fun invalidateDashboardCache() {
        dashboardReportsCache = null
        dashboardReportsV2Cache = null
    }

    // Fetch incidences via the shared API — used by DashboardModel to compute
    // accurate max impact dates (server always returns 1970-01-01 for these fields)
    suspend fun certificateActivities(
        from: DateTime,
        to: DateTime
    ): List<ApiAnswerProjectWithThunderActivities> {
        val api = states.value.currentApi ?: return emptyList()
        return api.certificateActivities(from, to)
    }

    // ── Thunder detection estimates ───────────────────────────────────────────

    private val estimates = mutableMapOf<String, ThunderDetectionEstimate>()

    fun estimate(uuid: String) = estimates[uuid]

    fun cacheEstimate(estimate: ThunderDetectionEstimate) {
        estimates[estimate.uuid] = estimate
    }
}

class AppModelManager(isSystemDarkMode: Boolean) : AppModel(isSystemDarkMode) {
    override val warnCustomerText: StringResource
        get() = Res.string.warn_customer_text_manager

    override fun warnCustomerAction() {
        // not yet implemented
    }
}
